// Ausschlusswörter eines Suchprofils (`excludedTitleTerms`).
//
// Der Server prüft sie NUR gegen den Titel einer Ausschreibung (tri-server
// docs/tender-search-profiles.md) — das muss am Feld unübersehbar stehen, sonst
// erwartet man, dass ein Wort aus der Beschreibung ausschließt. Deshalb steht
// „nur im Titel“ in der Überschrift selbst, nicht bloß in der Erklärung.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import FieldHelpButton from "./FieldHelpButton";

interface Properties {
  value: string[];
  onChange: (terms: string[]) => void;
  onHelp?: () => void;
}

/** Ein Wort wie gespeichert: getrimmt, ohne `*` (der Server ignoriert es ohnehin). */
const cleanTerm = (raw: string) => raw.replace(/\*/g, "").replace(/\s+/g, " ").trim();

export default function ExcludedTitleTermsInput({ value, onChange, onHelp }: Properties) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");

  const commit = () => {
    const additions = draft.split(/[,;\n]/).map(cleanTerm).filter(Boolean);
    setDraft("");
    if (!additions.length) return;
    const seen = new Set(value.map((term) => term.toLowerCase()));
    const next = [...value];
    for (const term of additions) {
      if (seen.has(term.toLowerCase())) continue;
      seen.add(term.toLowerCase());
      next.push(term);
    }
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid="excluded-title-terms">
      <div className="flex items-center gap-1">
        <label className="text-sm font-medium leading-none" htmlFor="excluded-title-terms-input">
          {t("tenderSearchProfiles.exclusions.label")}
        </label>
        {onHelp && <FieldHelpButton onClick={onHelp} />}
      </div>
      <p className="text-xs text-muted-foreground">{t("tenderSearchProfiles.exclusions.hint")}</p>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input p-2">
        {value.map((term, index) => (
          <span
            key={`${term}-${index}`}
            className="inline-flex items-center gap-1 rounded bg-destructive/10 px-2 py-0.5 text-sm text-destructive"
          >
            <span className="text-xs font-semibold" aria-hidden>
              {t("tenderSearchProfiles.exclusions.chipPrefix")}
            </span>
            {term}
            <button
              type="button"
              className="inline-flex cursor-pointer items-center justify-center rounded p-0.5 hover:text-foreground"
              aria-label={t("tenderSearchProfiles.exclusions.remove", { term })}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          id="excluded-title-terms-input"
          className="h-7 min-w-[12rem] flex-1 rounded border border-input bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground focus:border-ring"
          placeholder={t("tenderSearchProfiles.exclusions.placeholder")}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault();
              commit();
            }
          }}
          onBlur={commit}
        />
      </div>
    </div>
  );
}
